import React, { Suspense, useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import type { ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { token } from '@atlaskit/tokens';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { RovoAgentConversationStartersOld } from '@atlassian/rovo-conversation-actions/rovo-agent-conversation-starters-old';

import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useAgentVersioningConfig } from '../../../controllers/agent-versioning-config/useAgentVersioningConfig';
import { useChatStoreActions } from '../../../controllers/chat-store/useChatStoreActions';
import { AgentVersionPicker } from '../../agent-version-picker';
import { LegacyAgentVersionPickerWithCapability } from '../../agent-version-picker/LegacyAgentVersionPickerWithCapability';
import { RovoEmptyStateLoading } from '../rovo-empty-state-loading';

import { AgentVersionResolver, type ResolvedAgentVersionAccess } from './AgentVersionResolver';
import { RovoAgentHeader } from './rovo-agent-header';
import { RovoAgentConversationStartersNew } from './RovoAgentConversationStartersNew';

const styles = cssMap({
	wrapper: {
		height: '100%',
		width: '100%',
		marginTop: 'auto',
		paddingInline: token('space.200'),
		paddingBottom: token('space.200'),
	},
	wrapperFullscreen: {
		height: '100%',
		width: 'min(400px, 30vw)',
		marginTop: 'auto',
		marginInline: 'auto',
		paddingInline: token('space.0'),
		paddingBottom: token('space.200'),
	},
	conversationStarters: {
		paddingInline: token('space.200'),
		paddingBottom: token('space.200'),
		width: '100%',
	},
	conversationStartersFullscreen: {
		width: '100%',
	},
});

type RovoAgentEmptyStateProps = {
	agent: Agent;
	addCurrentPageAsContextUserPreference?: boolean;
};

type RovoAgentEmptyStateWithRelayProps = {
	agent: Agent;
	fullScreen: boolean;
	onConversationStarterClick: (conversationStarter: ConversationStarter) => void;
};

const RovoAgentEmptyStateWithRelay = ({
	agent,
	fullScreen,
	onConversationStarterClick,
}: RovoAgentEmptyStateWithRelayProps): React.JSX.Element => {
	di(
		useAgentVersioningConfig,
		AgentVersionResolver,
		AgentVersionPicker,
		RovoAgentHeader,
		RovoAgentConversationStartersNew,
		RovoEmptyStateLoading,
	);
	const showVersionPicker = useAgentVersioningConfig()?.showVersionPicker ?? false;
	const renderRelayContent = (resolvedAgentVersionAccess?: ResolvedAgentVersionAccess) => {
		const versionPicker =
			showVersionPicker &&
			resolvedAgentVersionAccess?.hasVersionCapability &&
			resolvedAgentVersionAccess.canManage &&
			resolvedAgentVersionAccess.agentAri ? (
				<AgentVersionPicker
					agentAri={resolvedAgentVersionAccess.agentAri}
					canManage={resolvedAgentVersionAccess.canManage}
				/>
			) : undefined;

		return (
			<Stack
				alignBlock="end"
				alignInline="center"
				xcss={fullScreen ? styles.wrapperFullscreen : styles.wrapper}
				testId="rovo-agent-empty-state"
				space="space.400"
			>
				<RovoAgentHeader agent={agent} versionPicker={versionPicker} />
				<Box
					xcss={fullScreen ? styles.conversationStartersFullscreen : styles.conversationStarters}
				>
					<RovoAgentConversationStartersNew
						agentId={agent.id}
						agentAri={agent.agentAri}
						onConversationStarterClick={onConversationStarterClick}
					/>
				</Box>
			</Stack>
		);
	};

	const relayFallback = (
		<Stack
			alignBlock="end"
			alignInline="center"
			xcss={fullScreen ? styles.wrapperFullscreen : styles.wrapper}
			testId="rovo-agent-empty-state"
			space="space.400"
		>
			<RovoAgentHeader agent={agent} />
			<Box xcss={fullScreen ? styles.conversationStartersFullscreen : styles.conversationStarters}>
				<RovoEmptyStateLoading showGreeting={false} />
			</Box>
		</Stack>
	);

	return (
		<Suspense fallback={relayFallback}>
			{showVersionPicker ? (
				<AgentVersionResolver agentId={agent.id} agentAri={agent.agentAri} fallback={relayFallback}>
					{renderRelayContent}
				</AgentVersionResolver>
			) : (
				renderRelayContent()
			)}
		</Suspense>
	);
};

export const RovoAgentEmptyState = ({
	agent,
	addCurrentPageAsContextUserPreference,
}: RovoAgentEmptyStateProps): React.JSX.Element => {
	di(
		fg,
		useEditorPlaceholder,
		useUIConfig,
		useAgentVersioningConfig,
		useChatStoreActions,
		LegacyAgentVersionPickerWithCapability,
		RovoAgentHeader,
		RovoAgentConversationStartersOld,
		RovoEmptyStateLoading,
	);
	const { insertPlaceholder, isPromptWithPlaceholder, clearPlaceholder } = useEditorPlaceholder();
	const { sendMessage } = useChatStoreActions();
	const { fullScreen } = useUIConfig();
	const showLegacyVersionPicker = useAgentVersioningConfig()?.showVersionPicker ?? false;

	const onConversationStarterClick = useCallback(
		async (conversationStarter: ConversationStarter) => {
			clearPlaceholder();

			if (isPromptWithPlaceholder(conversationStarter.message)) {
				insertPlaceholder(conversationStarter.message);
				return;
			}

			await sendMessage({
				prompt: encodeAdf(conversationStarter.message),
				storeUserMessages: false,
				agent,
				messageSources: {
					interactionSource: 'emptyStateSuggestions',
					followUpSource: 'ai-mate',
				},
				...(fg('rovo_agent_conversation_starter_context_preference')
					? {}
					: { addCurrentPageAsContextUserPreference }),
			});
		},
		[
			clearPlaceholder,
			insertPlaceholder,
			isPromptWithPlaceholder,
			agent,
			sendMessage,
			addCurrentPageAsContextUserPreference,
		],
	);

	if (!fg('rovo_agent_conversation_starters_relay')) {
		return (
			<Suspense fallback={<RovoEmptyStateLoading />}>
				<Stack
					alignBlock="end"
					alignInline="center"
					xcss={fullScreen ? styles.wrapperFullscreen : styles.wrapper}
					testId="rovo-agent-empty-state"
					space="space.400"
				>
					<RovoAgentHeader
						agent={agent}
						versionPicker={
							showLegacyVersionPicker ? <LegacyAgentVersionPickerWithCapability /> : undefined
						}
					/>
					<Box
						xcss={fullScreen ? styles.conversationStartersFullscreen : styles.conversationStarters}
					>
						<RovoAgentConversationStartersOld
							agent={agent}
							onConversationStarterClick={onConversationStarterClick}
						/>
					</Box>
				</Stack>
			</Suspense>
		);
	}

	return (
		<RovoAgentEmptyStateWithRelay
			agent={agent}
			fullScreen={Boolean(fullScreen)}
			onConversationStarterClick={onConversationStarterClick}
		/>
	);
};
